import { useState } from "react";
import { motion } from "framer-motion";
import { format } from "date-fns";
import { Calendar as CalendarIcon } from "lucide-react";
import { toast } from "sonner";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import { cn } from "@/lib/utils";

const WEDDING_TYPES = ["Wedding", "Rehearsal Dinner", "Elopement", "Corporate", "Private Party"];
const TASTING_TYPES = [
    { label: "Royal Tasting — $50", value: "Tasting: Royal" },
    { label: "Noble Tasting — $40", value: "Tasting: Noble" },
    { label: "Independent Tasting — $30", value: "Tasting: Independent" },
];

export default function Booking() {
    const [mode, setMode] = useState("wedding"); // 'wedding' | 'tasting'
    const [date, setDate] = useState(null);
    const [eventType, setEventType] = useState("Wedding");
    const [loading, setLoading] = useState(false);

    const switchMode = (m) => {
        setMode(m);
        setEventType(m === "wedding" ? "Wedding" : "Tasting: Noble");
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        const form = new FormData(e.currentTarget);
        const payload = {
            couple_names: form.get("couple_names")?.toString().trim() || "",
            email: form.get("email")?.toString().trim() || "",
            phone: form.get("phone")?.toString().trim() || "",
            event_type: eventType,
            event_date: date ? format(date, "yyyy-MM-dd") : null,
            guest_count: form.get("guest_count")
                ? Number(form.get("guest_count"))
                : null,
            message: form.get("message")?.toString() || "",
            source: mode === "tasting" ? "form-tasting" : "form",
        };

        if (!payload.couple_names || !payload.email) {
            toast.error("Please share your name and email so we can reach you.");
            return;
        }

        setLoading(true);
        try {
            await api.post("/inquiries", payload);
            toast.success(
                mode === "tasting"
                    ? "Thank you. We will confirm your tasting reservation shortly."
                    : "Thank you. The events team will be in touch within 24 hours.",
            );
            e.currentTarget.reset();
            setDate(null);
            setEventType(mode === "wedding" ? "Wedding" : "Tasting: Noble");
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Something went wrong.");
        } finally {
            setLoading(false);
        }
    };

    const isWedding = mode === "wedding";

    return (
        <section
            id="booking"
            className="section-py container-px bg-[hsl(var(--muted))]"
            data-testid="booking-section"
        >
            <div className="grid lg:grid-cols-12 gap-12 lg:gap-20">
                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                    viewport={{ once: true, margin: "-80px" }}
                    className="lg:col-span-5"
                >
                    <p className="overline">Begin the conversation</p>
                    <h2 className="mt-6 font-serif text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight leading-[1.05]">
                        {isWedding ? (
                            <>Tell us about <em className="italic text-[hsl(var(--primary))]">your day.</em></>
                        ) : (
                            <>Reserve your <em className="italic text-[hsl(var(--primary))]">tasting.</em></>
                        )}
                    </h2>
                    <p className="mt-6 text-muted-foreground text-lg font-light leading-relaxed">
                        {isWedding
                            ? "Share a few details and our events team will follow up within 24 hours with a personalised proposal, available dates and a private tour of the estate."
                            : "Reserve a guided Atanasio Tequila Tasting at the hacienda. Tastings run Thursday–Saturday; Monday–Wednesday by appointment."}
                    </p>
                    <div className="mt-10 text-sm text-muted-foreground tracking-wide space-y-1">
                        <p>gallery@lopezhill.com</p>
                        <p>12649 TX-71, Spicewood, TX 78669</p>
                    </div>
                </motion.div>

                <motion.form
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, delay: 0.1 }}
                    viewport={{ once: true, margin: "-80px" }}
                    onSubmit={onSubmit}
                    className="lg:col-span-7 bg-background p-8 sm:p-12 border border-border"
                    data-testid="booking-form"
                >
                    <div
                        className="grid grid-cols-2 mb-10 border border-border"
                        data-testid="booking-mode-toggle"
                    >
                        <button
                            type="button"
                            onClick={() => switchMode("wedding")}
                            className={cn(
                                "py-3 text-xs tracking-[0.25em] uppercase transition-colors duration-300",
                                isWedding
                                    ? "bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]"
                                    : "bg-transparent text-muted-foreground hover:text-foreground",
                            )}
                            data-testid="mode-wedding"
                        >
                            Wedding & Events
                        </button>
                        <button
                            type="button"
                            onClick={() => switchMode("tasting")}
                            className={cn(
                                "py-3 text-xs tracking-[0.25em] uppercase transition-colors duration-300",
                                !isWedding
                                    ? "bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]"
                                    : "bg-transparent text-muted-foreground hover:text-foreground",
                            )}
                            data-testid="mode-tasting"
                        >
                            Tequila Tasting
                        </button>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-8">
                        <Field
                            label={isWedding ? "Couple / Host Names" : "Your Name"}
                            name="couple_names"
                            required
                            testId="input-couple-names"
                        />
                        <Field label="Email" name="email" type="email" required testId="input-email" />
                        <Field label="Phone" name="phone" type="tel" testId="input-phone" />
                        <Field
                            label={isWedding ? "Guest Count" : "Number of Guests"}
                            name="guest_count"
                            type="number"
                            testId="input-guests"
                        />

                        <div className="flex flex-col gap-2">
                            <label className="overline text-[hsl(var(--muted-foreground))]">
                                {isWedding ? "Event Type" : "Tasting Package"}
                            </label>
                            <Select value={eventType} onValueChange={setEventType}>
                                <SelectTrigger
                                    className="rounded-none border-0 border-b border-border bg-transparent px-0 h-12 focus:ring-0 focus:border-[hsl(var(--primary))]"
                                    data-testid="select-event-type"
                                >
                                    <SelectValue placeholder="Select" />
                                </SelectTrigger>
                                <SelectContent>
                                    {isWedding
                                        ? WEDDING_TYPES.map((t) => (
                                              <SelectItem
                                                  key={t}
                                                  value={t}
                                                  data-testid={`event-type-${t.toLowerCase().replace(/ /g, "-")}`}
                                              >
                                                  {t}
                                              </SelectItem>
                                          ))
                                        : TASTING_TYPES.map((t) => (
                                              <SelectItem
                                                  key={t.value}
                                                  value={t.value}
                                                  data-testid={`tasting-${t.value.toLowerCase().replace(/[: ]+/g, "-")}`}
                                              >
                                                  {t.label}
                                              </SelectItem>
                                          ))}
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="flex flex-col gap-2">
                            <label className="overline text-[hsl(var(--muted-foreground))]">
                                {isWedding ? "Preferred Date" : "Preferred Tasting Date"}
                            </label>
                            <Popover>
                                <PopoverTrigger asChild>
                                    <button
                                        type="button"
                                        className={cn(
                                            "h-12 flex items-center justify-between border-b border-border text-left text-base font-light",
                                            !date && "text-muted-foreground",
                                        )}
                                        data-testid="date-picker-trigger"
                                    >
                                        {date ? format(date, "EEEE, MMMM d, yyyy") : "Pick a date"}
                                        <CalendarIcon className="h-4 w-4 opacity-60" />
                                    </button>
                                </PopoverTrigger>
                                <PopoverContent className="p-0 w-auto" align="start">
                                    <Calendar
                                        mode="single"
                                        selected={date}
                                        onSelect={setDate}
                                        disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                                        initialFocus
                                    />
                                </PopoverContent>
                            </Popover>
                        </div>
                    </div>

                    <div className="mt-10 flex flex-col gap-2">
                        <label className="overline text-[hsl(var(--muted-foreground))]">
                            {isWedding ? "Tell us your story" : "Anything else we should know?"}
                        </label>
                        <textarea
                            name="message"
                            rows={4}
                            className="border-b border-border bg-transparent py-3 text-base font-light focus:outline-none focus:border-[hsl(var(--primary))] resize-none"
                            placeholder={
                                isWedding
                                    ? "Anything we should know — vision, mood, must-haves…"
                                    : "Allergies, dietary notes, special occasion…"
                            }
                            data-testid="input-message"
                        />
                    </div>

                    <button
                        type="submit"
                        disabled={loading}
                        className="mt-12 px-10 py-4 bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:bg-[hsl(var(--primary-hover))] transition-all duration-300 text-xs tracking-[0.3em] uppercase disabled:opacity-60"
                        data-testid="submit-inquiry-btn"
                    >
                        {loading
                            ? "Sending…"
                            : isWedding
                              ? "Send Inquiry"
                              : "Reserve Tasting"}
                    </button>
                </motion.form>
            </div>
        </section>
    );
}

function Field({ label, name, type = "text", required, testId }) {
    return (
        <div className="flex flex-col gap-2">
            <label className="overline text-[hsl(var(--muted-foreground))]">
                {label}
                {required && " *"}
            </label>
            <input
                name={name}
                type={type}
                required={required}
                className="h-12 border-b border-border bg-transparent text-base font-light focus:outline-none focus:border-[hsl(var(--primary))]"
                data-testid={testId}
            />
        </div>
    );
}
